import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { ExternalLink, QrCode, Copy } from 'lucide-react';

interface ContextMenuProps {
  x: number;
  y: number;
  url: string;
  onClose: () => void;
  theme?: 'light' | 'dark';
}

const MenuContainer = styled.div<{ theme?: 'light' | 'dark' }>`
  position: fixed;
  background: ${props => props.theme === 'dark' ? '#242424' : 'white'};
  border-radius: 8px;
  box-shadow: 0 2px 10px ${props => props.theme === 'dark' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(0, 0, 0, 0.1)'};
  padding: 8px 0;
  min-width: 180px;
  z-index: 1000;
  border: 1px solid ${props => props.theme === 'dark' ? '#333' : '#eaeaea'};
  color: ${props => props.theme === 'dark' ? '#e0e0e0' : 'inherit'};
`;

const MenuItem = styled.div<{ theme?: 'light' | 'dark' }>`
  padding: 8px 16px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: ${props => props.theme === 'dark' ? '#333' : '#f5f5f5'};
  }
  
  svg {
    margin-right: 8px;
    width: 16px;
    height: 16px;
  }
`;

const Divider = styled.div<{ theme?: 'light' | 'dark' }>`
  height: 1px;
  background: ${props => props.theme === 'dark' ? '#333' : '#eaeaea'};
  margin: 4px 0;
`;

const QrCodeModal = styled.div<{ theme?: 'light' | 'dark' }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
`;

const QrCodeContent = styled.div<{ theme?: 'light' | 'dark' }>`
  background-color: ${props => props.theme === 'dark' ? '#242424' : 'white'};
  padding: 20px;
  border-radius: 8px;
  text-align: center;
  color: ${props => props.theme === 'dark' ? '#e0e0e0' : 'inherit'};
`;

export const ContextMenu: React.FC<ContextMenuProps> = ({ x, y, url, onClose, theme = 'light' }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [qrCodeVisible, setQrCodeVisible] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  
  // 处理点击菜单外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);
  
  // 处理打开链接
  const handleOpenLink = () => {
    window.open(url, '_blank');
    onClose();
  };
  
  // 处理显示二维码
  const handleShowQrCode = () => {
    // 使用 QR Code API 生成二维码
    const encodedUrl = encodeURIComponent(url);
    setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodedUrl}`);
    setQrCodeVisible(true);
    // 不要关闭菜单，否则二维码模态框也会被卸载
    // onClose();
  };
  
  // 处理复制链接
  const handleCopyLink = () => {
    navigator.clipboard.writeText(url).then(() => {
      // 可以添加一个提示，表示复制成功
      onClose();
    });
  };
  
  return (
    <>
      {!qrCodeVisible && (
        <MenuContainer 
          ref={menuRef} 
          theme={theme}
          style={{ 
            left: x, 
            top: y,
            // 确保菜单不会超出屏幕
            transform: `translate(${x + 180 > window.innerWidth ? '-100%' : '0'}, ${y + 150 > window.innerHeight ? '-100%' : '0'})` 
          }}
        >
          <MenuItem theme={theme} onClick={handleOpenLink}>
            <ExternalLink /> 打开链接
          </MenuItem>
          <MenuItem theme={theme} onClick={handleShowQrCode}>
            <QrCode /> 链接二维码
          </MenuItem>
          <Divider theme={theme} />
          <MenuItem theme={theme} onClick={handleCopyLink}>
            <Copy /> 复制链接
          </MenuItem>
        </MenuContainer>
      )}
      
      {qrCodeVisible && (
        <QrCodeModal 
          theme={theme}
          onClick={() => {
            setQrCodeVisible(false);
            onClose();
          }}
        >
          <QrCodeContent 
            theme={theme}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>链接二维码</h3>
            <img src={qrCodeUrl} alt="QR Code" style={{ width: '200px', height: '200px' }} />
            <p style={{ marginBottom: 0, wordBreak: 'break-all', maxWidth: '300px' }}>{url}</p>
          </QrCodeContent>
        </QrCodeModal>
      )}
    </>
  );
}; 